import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthPayload, getUserRoles, getUserEmail } from '../utils/auth';

const STATUS_COLORS = {
  Approved: 'var(--color-success)',
  Rejected: 'var(--color-error)',
  Denied: 'var(--color-error)',
  Pending: 'var(--color-warning)',
  'Under Review': 'var(--color-primary-light)',
};

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const DASHBOARD_TITLE_STORAGE_KEY = 'dashboardPageTitle';
const DASHBOARD_YEAR_STORAGE_KEY = 'selectedDashboardYear';
const DASHBOARD_YEAR_OPTIONS_STORAGE_KEY = 'dashboardYearOptions';
const DEFAULT_DASHBOARD_FISCAL_YEAR = 2022;

function publishDashboardTitle(title) {
  window.sessionStorage.setItem(DASHBOARD_TITLE_STORAGE_KEY, title);
  window.dispatchEvent(new CustomEvent('dashboard-title-change', { detail: title }));
}

function publishDashboardYear(selectedYear, options) {
  window.sessionStorage.setItem(DASHBOARD_YEAR_STORAGE_KEY, selectedYear ? String(selectedYear) : '');
  window.sessionStorage.setItem(DASHBOARD_YEAR_OPTIONS_STORAGE_KEY, JSON.stringify(options || []));
  window.dispatchEvent(
    new CustomEvent('dashboard-year-change', {
      detail: {
        selectedYear: selectedYear ? String(selectedYear) : '',
        options: options || [],
      },
    })
  );
}

// US Federal Fiscal Year: Oct 1 – Sep 30
function getCurrentFiscalYear() {
  const now = new Date();
  return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
}

function appFiscalYear(grant) {
  if (grant.year) {
    const y = parseInt(grant.year, 10);
    if (!isNaN(y)) return y;
  }
  if (grant.submittedAt) {
    const d = new Date(grant.submittedAt);
    return d.getMonth() >= 9 ? d.getFullYear() + 1 : d.getFullYear();
  }
  return null;
}

function formatCurrency(amount) {
  if (typeof amount === 'number') return `$${amount.toLocaleString()}`;
  const n = parseFloat(amount);
  return isNaN(n) ? (amount || '-') : `$${n.toLocaleString()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return isNaN(d) ? '-' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Returns true if the reviewer email appears in any assigned review slot
function isAssignedToReviewer(grant, email) {
  if (!email) return false;
  const lc = email.toLowerCase();
  if (Array.isArray(grant.formalReviews)) {
    if (grant.formalReviews.some((r) => r.reviewerEmail?.toLowerCase() === lc)) return true;
  }
  if (Array.isArray(grant.reviewRounds)) {
    if (grant.reviewRounds.some((r) => (r.assignedReviewers || '').toLowerCase().includes(lc))) return true;
  }
  return false;
}

const Dashboard = () => {
  const [grants, setGrants] = useState([]);
  const [grantsError, setGrantsError] = useState('');
  const [programsList, setProgramsList] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(() => {
    const stored = window.sessionStorage.getItem('selectedDashboardProgram') || '';
    try {
      const parsed = JSON.parse(stored);
      return parsed?.programCode || parsed?.id || '';
    } catch {
      return stored;
    }
  });
  const [fiscalYear, setFiscalYear] = useState(() => {
    window.sessionStorage.setItem(DASHBOARD_YEAR_STORAGE_KEY, String(DEFAULT_DASHBOARD_FISCAL_YEAR));
    return DEFAULT_DASHBOARD_FISCAL_YEAR;
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [nameSearch, setNameSearch] = useState('');

  const token = localStorage.getItem('token');
  const payload = getAuthPayload();
  const roles = getUserRoles(payload);
  const userEmail = getUserEmail(payload);

  const isAdmin = roles.includes('Super Admin');
  const isManager = roles.includes('Grant Manager');
  const isReviewer = roles.includes('Internal Reviewer');
  const canManage = isAdmin || isManager;

  const navigate = useNavigate();

  const selectedProgramMeta = useMemo(
    () => programsList.find((program) => program.programCode === selectedProgram) || null,
    [programsList, selectedProgram]
  );

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/programs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setProgramsList(data))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/applications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data) => { setGrants(data); setGrantsError(''); })
      .catch(() => setGrantsError('Could not load grant applications.'));
  }, [token]);

  const programScopedGrants = useMemo(() => {
    if (!selectedProgram) return grants;
    const selectedProgramName = (selectedProgramMeta?.name || '').toLowerCase();
    return grants.filter((grant) => {
      const programCode = (grant.programCode || '').toLowerCase();
      const grantProgram = (grant.grantProgram || '').toLowerCase();
      return programCode === selectedProgram.toLowerCase()
        || grantProgram === selectedProgram.toLowerCase()
        || (selectedProgramName && grantProgram === selectedProgramName);
    });
  }, [grants, selectedProgram, selectedProgramMeta]);

  const availableFYs = useMemo(() => {
    const years = new Set([DEFAULT_DASHBOARD_FISCAL_YEAR, getCurrentFiscalYear()]);
    programScopedGrants.forEach((g) => { const fy = appFiscalYear(g); if (fy) years.add(fy); });
    return Array.from(years).sort((a, b) => b - a);
  }, [programScopedGrants]);

  const fyGrants = useMemo(
    () => (fiscalYear ? programScopedGrants.filter((g) => appFiscalYear(g) === fiscalYear) : programScopedGrants),
    [programScopedGrants, fiscalYear],
  );

  const dashboardHeading = useMemo(() => {
    const baseTitle = selectedProgramMeta
      ? `${selectedProgramMeta.name || selectedProgramMeta.programCode} applications`
      : 'All grant applications';
    return fiscalYear ? `${baseTitle} · FY${fiscalYear}` : baseTitle;
  }, [fiscalYear, selectedProgramMeta]);

  useEffect(() => {
    publishDashboardTitle(dashboardHeading);
  }, [dashboardHeading]);

  useEffect(() => {
    publishDashboardYear(fiscalYear, availableFYs);
  }, [fiscalYear, availableFYs]);

  useEffect(() => {
    const syncYearFromHeader = (event) => {
      const nextYear = event?.detail?.selectedYear || window.sessionStorage.getItem(DASHBOARD_YEAR_STORAGE_KEY) || '';
      const parsed = nextYear ? parseInt(nextYear, 10) : NaN;
      setFiscalYear(Number.isFinite(parsed) ? parsed : null);
    };

    window.addEventListener('dashboard-year-change', syncYearFromHeader);
    return () => window.removeEventListener('dashboard-year-change', syncYearFromHeader);
  }, []);

  useEffect(
    () => () => {
      window.sessionStorage.removeItem(DASHBOARD_TITLE_STORAGE_KEY);
      window.sessionStorage.removeItem(DASHBOARD_YEAR_STORAGE_KEY);
      window.sessionStorage.removeItem(DASHBOARD_YEAR_OPTIONS_STORAGE_KEY);
      window.dispatchEvent(new CustomEvent('dashboard-title-change', { detail: '' }));
      window.dispatchEvent(
        new CustomEvent('dashboard-year-change', {
          detail: { selectedYear: '', options: [] },
        })
      );
    },
    []
  );

  // ── Admin / Manager metrics ──────────────────────────────────────────────
  const adminSummary = useMemo(() => {
    const approved = fyGrants.filter((g) => g.status === 'Approved').length;
    const pending = fyGrants.filter((g) => g.status === 'Pending').length;
    const underReview = fyGrants.filter((g) => g.status === 'Under Review').length;
    const denied = fyGrants.filter((g) => g.status === 'Rejected' || g.status === 'Denied').length;
    const approvedFunding = fyGrants
      .filter((g) => g.status === 'Approved')
      .reduce((sum, g) => sum + (typeof g.fundingAmount === 'number' ? g.fundingAmount : parseFloat(g.fundingAmount) || 0), 0);
    return { total: fyGrants.length, approved, pending, underReview, denied, approvedFunding };
  }, [fyGrants]);

  // Oldest-first pending apps for admin "Needs Attention"
  const needsAttention = useMemo(
    () => fyGrants
      .filter((g) => g.status === 'Pending')
      .sort((a, b) => new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0))
      .slice(0, 6),
    [fyGrants],
  );

  // ── Reviewer metrics ─────────────────────────────────────────────────────
  const myAssignments = useMemo(
    () => isReviewer && !canManage
      ? fyGrants.filter((g) => isAssignedToReviewer(g, userEmail))
      : [],
    [fyGrants, isReviewer, canManage, userEmail],
  );

  // Active = assigned to me AND still Under Review
  const myActiveAssignments = useMemo(
    () => myAssignments
      .filter((g) => g.status === 'Under Review')
      .sort((a, b) => new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0))
      .slice(0, 6),
    [myAssignments],
  );

  // Fallback when no explicit assignments: show all Under Review (oldest first)
  const generalReviewQueue = useMemo(
    () => fyGrants
      .filter((g) => g.status === 'Under Review')
      .sort((a, b) => new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0))
      .slice(0, 6),
    [fyGrants],
  );

  const reviewerSummary = useMemo(() => ({
    myAssignments: myAssignments.length,
    activeQueue: fyGrants.filter((g) => g.status === 'Under Review').length,
    pendingIntake: fyGrants.filter((g) => g.status === 'Pending').length,
    completed: fyGrants.filter((g) => ['Approved', 'Denied', 'Rejected'].includes(g.status)).length,
  }), [fyGrants, myAssignments]);

  // ── Shared table filter ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = nameSearch.toLowerCase();
    return fyGrants.filter((g) => {
      const grantName = (g.projectName || '').toLowerCase();
      const applicant = `${g.firstName || ''} ${g.lastName || ''}`.trim().toLowerCase();
      const matchesName = !q || grantName.includes(q) || applicant.includes(q);
      const matchesStatus = !statusFilter || g.status === statusFilter;
      return matchesName && matchesStatus;
    });
  }, [fyGrants, nameSearch, statusFilter]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleProgramChange = (value) => {
    setSelectedProgram(value);
    window.sessionStorage.setItem('selectedDashboardProgram', value);
    const current = JSON.parse(window.sessionStorage.getItem('newApplicationData') || '{}');
    const chosen = programsList.find((p) => p.programCode === value);
    if (chosen) {
      window.sessionStorage.setItem('newApplicationData', JSON.stringify({
        ...current,
        program: { id: chosen.id, programCode: chosen.programCode, name: chosen.name },
      }));
    } else if (!value) {
      const copy = { ...current };
      delete copy.program;
      window.sessionStorage.setItem('newApplicationData', JSON.stringify(copy));
    }
  };

  const handleStartApplication = () => {
    if (!selectedProgram) {
      alert('Please select a program before creating an application.');
      return;
    }
    handleProgramChange(selectedProgram);
    navigate('/dashboard/new-application/documents');
  };

  // ── Shared application row ────────────────────────────────────────────────
  const AppRow = ({ grant, cols }) => (
    <tr
      key={grant.id || grant._id || grant.applicationNumber}
      style={{ cursor: grant.id || grant._id ? 'pointer' : 'default' }}
      onClick={() => (grant.id || grant._id) && navigate(`/dashboard/applications/${grant.id || grant._id}`)}
    >
      <td><span className="app-id">{grant.applicationNumber || '-'}</span></td>
      <td>{grant.projectName || '-'}</td>
      <td>{`${grant.firstName || ''} ${grant.lastName || ''}`.trim() || grant.legalName || '-'}</td>
      <td>{grant.programCode || grant.grantProgram || '-'}</td>
      <td>{formatCurrency(grant.fundingAmount)}</td>
      {cols === 'attention' ? (
        <td>{formatDate(grant.submittedAt)}</td>
      ) : (
        <>
          <td>
            <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[grant.status] || 'var(--text-secondary)' }}>
              {grant.status}
            </span>
          </td>
          <td>{formatDate(grant.submittedAt)}</td>
        </>
      )}
    </tr>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page-stack">
      {/* ── ADMIN / MANAGER VIEW ─────────────────────────────────────────── */}
      {canManage && (
        <>
          {/* 6-card stats */}
          <section className="stats-grid">
            <div className="stat-card stat-card--total">
              <span className="stat-label">Total</span>
              <strong>{adminSummary.total}</strong>
              <p>{fiscalYear ? `Applications in FY${fiscalYear}` : 'All applications'}</p>
            </div>
            <div className="stat-card stat-card--pending">
              <span className="stat-label">Pending</span>
              <strong>{adminSummary.pending}</strong>
              <p>Awaiting assignment or first review</p>
            </div>
            <div className="stat-card stat-card--review">
              <span className="stat-label">Under Review</span>
              <strong>{adminSummary.underReview}</strong>
              <p>Actively in the review pipeline</p>
            </div>
            <div className="stat-card stat-card--approved">
              <span className="stat-label">Approved</span>
              <strong>{adminSummary.approved}</strong>
              <p>Positive outcome recorded</p>
            </div>
            <div className="stat-card stat-card--denied">
              <span className="stat-label">Denied</span>
              <strong>{adminSummary.denied}</strong>
              <p>Not selected for funding</p>
            </div>
            <div className="stat-card stat-card--funding">
              <span className="stat-label">Approved Funding</span>
              <strong className="stat-value-funding">{formatCurrency(adminSummary.approvedFunding)}</strong>
              <p>Total awarded amount</p>
            </div>
          </section>

          {/* Needs Attention */}
          {needsAttention.length > 0 && (
            <section className="dashboard-section attention-section">
              <div className="section-top">
                <div>
                  <h2>Needs Attention</h2>
                  <p className="table-meta">
                    {adminSummary.pending} pending application{adminSummary.pending !== 1 ? 's' : ''} awaiting
                    assignment — oldest submissions first
                  </p>
                </div>
                <button className="view-all-btn" onClick={() => navigate('/dashboard/applications')}>
                  View All Pending
                </button>
              </div>
              <div className="grants-table-wrapper">
                <table className="grants-table">
                  <thead>
                    <tr>
                      <th>Application #</th>
                      <th>Grant Name</th>
                      <th>Applicant</th>
                      <th>Program</th>
                      <th>Requested</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {needsAttention.map((grant) => (
                      <AppRow key={grant._id || grant.applicationNumber} grant={grant} cols="attention" />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {/* ── REVIEWER VIEW ────────────────────────────────────────────────── */}
      {!canManage && isReviewer && (
        <>
          {/* 4-card stats */}
          <section className="stats-grid stats-grid--reviewer">
            <div className="stat-card stat-card--review">
              <span className="stat-label">My Assignments</span>
              <strong>{reviewerSummary.myAssignments}</strong>
              <p>Applications assigned directly to you</p>
            </div>
            <div className="stat-card stat-card--pending">
              <span className="stat-label">Active Queue</span>
              <strong>{reviewerSummary.activeQueue}</strong>
              <p>Applications currently under review</p>
            </div>
            <div className="stat-card stat-card--total">
              <span className="stat-label">Pending Intake</span>
              <strong>{reviewerSummary.pendingIntake}</strong>
              <p>Not yet assigned — incoming work</p>
            </div>
            <div className="stat-card stat-card--approved">
              <span className="stat-label">Completed</span>
              <strong>{reviewerSummary.completed}</strong>
              <p>Approved or denied this period</p>
            </div>
          </section>

          {/* My Assignments panel (explicit) */}
          {myActiveAssignments.length > 0 ? (
            <section className="dashboard-section reviewer-queue-section">
              <div className="section-top">
                <div>
                  <h2>My Active Assignments</h2>
                  <p className="table-meta">
                    Applications assigned to you that are currently under review — oldest first
                  </p>
                </div>
                <button className="view-all-btn" onClick={() => navigate('/dashboard/applications')}>
                  Full List
                </button>
              </div>
              <div className="grants-table-wrapper">
                <table className="grants-table">
                  <thead>
                    <tr>
                      <th>Application #</th>
                      <th>Grant Name</th>
                      <th>Applicant</th>
                      <th>Program</th>
                      <th>Requested</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myActiveAssignments.map((grant) => (
                      <AppRow key={grant._id || grant.applicationNumber} grant={grant} cols="attention" />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            /* Fallback: general Under Review queue */
            generalReviewQueue.length > 0 && (
              <section className="dashboard-section reviewer-queue-section">
                <div className="section-top">
                  <div>
                    <h2>Active Review Queue</h2>
                    <p className="table-meta">
                      {reviewerSummary.activeQueue} application{reviewerSummary.activeQueue !== 1 ? 's' : ''} currently
                      under review — oldest submissions first
                    </p>
                  </div>
                  <button className="view-all-btn" onClick={() => navigate('/dashboard/applications')}>
                    Full List
                  </button>
                </div>
                <div className="grants-table-wrapper">
                  <table className="grants-table">
                    <thead>
                      <tr>
                        <th>Application #</th>
                        <th>Grant Name</th>
                        <th>Applicant</th>
                        <th>Program</th>
                        <th>Requested</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generalReviewQueue.map((grant) => (
                        <AppRow key={grant._id || grant.applicationNumber} grant={grant} cols="attention" />
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )
          )}
        </>
      )}

      {/* ── ALL-ROLES: Applications table ─────────────────────────────────── */}
      <section className="dashboard-section">
        <div className="section-top">
          <div>
            <h2>
              {canManage ? 'Grant Applications' : 'All Applications'}
            </h2>
            <p className="table-meta">
              {filtered.length} application{filtered.length !== 1 ? 's' : ''} shown
              {selectedProgram ? ` · ${selectedProgram}` : ''}
            </p>
          </div>
          <div className="section-actions">
            <button className="view-all-btn" onClick={() => navigate('/dashboard/applications')}>
              Full List
            </button>
            {canManage && (
              <button
                className="add-application-btn"
                onClick={handleStartApplication}
                disabled={!selectedProgram}
                title={!selectedProgram ? 'Select a program first' : 'Create a new application'}
              >
                New Application
              </button>
            )}
          </div>
        </div>

        <div className="inline-filters dashboard-app-filters">
          {canManage && (
            <div className="filter-group">
              <label className="filter-label">Program</label>
              <select
                className="search-input"
                style={{ marginBottom: 0 }}
                value={selectedProgram}
                onChange={(e) => handleProgramChange(e.target.value)}
              >
                <option value="">All Programs</option>
                {programsList.map((p) => (
                  <option key={p.id} value={p.programCode}>{p.programCode} – {p.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="search-input"
              style={{ marginBottom: 0 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Under Review">Under Review</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Denied / Rejected</option>
            </select>
          </div>
          <div className={`filter-group${canManage ? '' : ' dashboard-filter-wide'}`}>
            <label className="filter-label">Search</label>
            <input
              type="text"
              className="search-input"
              style={{ marginBottom: 0 }}
              placeholder="Grant name or applicant..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
          </div>
        </div>

        {grantsError && <p className="section-error">{grantsError}</p>}

        {!grantsError && (
          <div className="grants-table-wrapper">
            <table className="grants-table">
              <thead>
                <tr>
                  <th>Application #</th>
                  <th>Grant Name</th>
                  <th>Applicant</th>
                  <th>Program</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-results">No applications match your filters.</td>
                  </tr>
                ) : (
                  filtered.map((grant) => (
                    <AppRow
                      key={grant._id || grant.applicationNumber}
                      grant={grant}
                      cols="full"
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
